import React from "react";
import { useQuery } from "react-query";
import {
  clearRedirectUrl,
  getRedirectUrl,
  loadIdentity,
  isAdminUser,
} from "../utils/AuthUtils";
import { logger } from "../utils/LogUtils";
import { Profile } from "../utils/types/profile";
import { profileModel } from "../utils/Model";

export type GroupView =
  | "admin"
  | "partner"
  | "partner admin"
  | "builder"
  | "tester"
  | "sales";

export type User = {
  admin: boolean;
  builder: boolean;
  partner: boolean;
  partnerAdmin: boolean;
  tester: boolean;
  city?: string;
  country?: string;
  displayName?: string;
  email: string;
  firstName: string;
  lastName: string;
  roleName?: string;
  userId?: string;
  originalUser?: User;
  currentGroupView?: GroupView;
};

interface AuthContextProps {
  user: User;
  profile: Profile | undefined;
  logout: () => void;
  refetchIdentity: () => void;
  amazonLogin: () => void;
  partnerLogin: () => void;
  switchGroupView: (groupView: GroupView) => void;
}

const AuthContext = React.createContext<AuthContextProps>({
  user: undefined,
  profile: undefined,
  logout: () => {},
  refetchIdentity: () => {},
  amazonLogin: () => {},
  partnerLogin: () => {},
  switchGroupView: () => {},
});

/**
 * Amazon login page
 */
function amazonLogin() {
  logger.info(
    "Authentication.amazonLogin() logging in with Amazon credentials"
  );
  logger.log(import.meta.env.REACT_APP_AMAZON_LOGIN);
  document.location.href = import.meta.env.REACT_APP_AMAZON_LOGIN;
}

/**
 * Partner login page
 */
function partnerLogin() {
  logger.info(
    "Authentication.partnerLogin() logging in with Partner credentials"
  );
  document.location.href = import.meta.env.REACT_APP_PARTNER_LOGIN;
}

/**
 * Checks for cookie 'demoportalidentity' presence
 * and redirects on error
 */
const Authentication = ({ children }: { children: React.ReactNode }) => {
  const [identityInbound, setIdentityInbound] = React.useState(undefined);
  const [user, setUser] = React.useState<User | undefined>(undefined);

  // Load the id token from the url or from local store
  React.useEffect(() => {
    const queryParams = new URLSearchParams(document.location.search);
    setIdentityInbound(queryParams.get("identityInbound"));
  }, []);

  // The identity token may come from the url params otherwise it is located from local store
  const {
    data: identity,
    isLoading: isLoadingIdentity,
    refetch: refetchIdentity,
  } = useQuery(
    ["identity", identityInbound],
    () => loadIdentity(identityInbound),
    {
      retry: 2,
      staleTime: 30000,
      cacheTime: 30000,
      refetchInterval: 60000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      initialData: undefined,
      onSuccess: (data) => {
        if (data?.user) {
          setUser(data.user);
        }
      },
    }
  );

  const { data: profile } = useQuery(
    ["profile"],
    () => {
      if (identity?.loggedIn) {
        return profileModel.getProfile();
      }
      return undefined;
    },
    {
      retry: 2,
      staleTime: 1000 * 60 * 60 * 24 * 365,
      cacheTime: 1000 * 60 * 60 * 24 * 365,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      initialData: undefined,
      enabled: !!identity,
    }
  );

  const switchGroupView = (groupView: GroupView) => {
    if (!user) return;

    const originalUser = user.originalUser || user;
    if (!isAdminUser(originalUser)) return;

    const simulatedUser = getSimulatedUser(originalUser, groupView);
    simulatedUser.originalUser = originalUser;
    simulatedUser.currentGroupView = groupView;

    setUser(simulatedUser);
  };

  if (isLoadingIdentity) {
    return null;
  }

  if (identity && identity.loggedIn && identity.navigateHome) {
    logger.info("Authentication() redirect required");
    const url = getRedirectUrl();
    window.location.href = url;
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        user: identity ? user : undefined,
        profile: profile ? profile : undefined,
        logout: logout,
        refetchIdentity,
        amazonLogin: amazonLogin,
        partnerLogin: partnerLogin,
        switchGroupView,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Logs out the user
 */
function logout() {
  localStorage.removeItem("identity");
  clearRedirectUrl();
  document.location.href = `${import.meta.env.REACT_APP_API_BASE}/api/logout?${import.meta.env.REACT_APP_AUTH_PARAMS}`;
  logger.info(`Logged out user`);
}

const getSimulatedUser = (adminUser: User, groupView: GroupView): User => {
  const simulatedUser = { ...adminUser };

  simulatedUser.admin = false;
  simulatedUser.builder = false;
  simulatedUser.partner = false;
  simulatedUser.partnerAdmin = false;
  simulatedUser.tester = false;

  switch (groupView) {
    case "admin":
      simulatedUser.admin = true;
      simulatedUser.builder = true;
      simulatedUser.partnerAdmin = true;
      break;
    case "partner":
      simulatedUser.partner = true;
      break;
    case "partner admin":
      simulatedUser.partnerAdmin = true;
      break;
    case "builder":
      simulatedUser.builder = true;
      break;
    case "tester":
      simulatedUser.tester = true;
      break;
    case "sales":
      break;
  }

  return simulatedUser;
};

export const useAuth = () => React.useContext(AuthContext);

export default Authentication;
